import { Monitor, Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { useTheme, type ThemeMode } from './ThemeProvider'

const modes: { value: ThemeMode; icon: typeof Sun; labelKey: string }[] = [
  { value: 'light', icon: Sun, labelKey: 'theme.light' },
  { value: 'dark', icon: Moon, labelKey: 'theme.dark' },
  { value: 'system', icon: Monitor, labelKey: 'theme.system' },
]

/**
 * Light / dark / system. Three explicit choices rather than a toggle: a toggle
 * cannot express "follow my device", which is what most people actually want.
 */
export function ThemeSwitcher({ className, onDark = false }: { className?: string; onDark?: boolean }) {
  const { mode, setMode } = useTheme()
  const { t } = useI18n()

  return (
    <div
      role="group"
      aria-label={t('theme.label')}
      data-upbo-id="theme-switcher"
      data-upbo-label="Theme switcher"
      className={cn(
        'inline-flex items-center p-0.5 border rounded-squircle',
        onDark ? 'border-white/25' : 'border-border',
        className,
      )}
    >
      {modes.map(({ value, icon: Icon, labelKey }) => (
        <button
          key={value}
          type="button"
          onClick={() => setMode(value)}
          aria-pressed={mode === value}
          title={t(labelKey)}
          aria-label={t(labelKey)}
          className={cn(
            'h-7 w-8 grid place-items-center rounded-squircle transition-colors',
            onDark
              ? mode === value
                ? 'bg-white/15 text-white'
                : 'text-white/55 hover:text-white'
              : mode === value
                ? 'bg-surface-2 text-text'
                : 'text-text-faint hover:text-text-muted',
          )}
        >
          <Icon size={14} strokeWidth={1.6} />
        </button>
      ))}
    </div>
  )
}

/** One-tap light↔dark, for the mobile drawer where space is tight. */
export function ThemeToggleButton({ className }: { className?: string }) {
  const { theme, toggle } = useTheme()
  const { t } = useI18n()
  const Icon = theme === 'dark' ? Sun : Moon

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={t('theme.label')}
      className={cn(
        'inline-flex items-center gap-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-text-muted hover:text-text transition-colors',
        className,
      )}
    >
      <Icon size={15} strokeWidth={1.5} />
      {theme === 'dark' ? t('theme.light') : t('theme.dark')}
    </button>
  )
}
